import { CommonModule } from '@angular/common';
import { Component, input, ChangeDetectionStrategy } from '@angular/core';
import { MatTooltip } from '@angular/material/tooltip';
import {
  DisplayableForContractStatusDirective,
  DisplayableForContractStatusInput,
} from '@app/shared/displayable-for-contract-status.directive';
import { TextIcon } from '@app/shared/icons/text-icon/text-icon';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faSignature } from '@fortawesome/free-solid-svg-icons';
import { LucideMinus } from '@lucide/angular';
import { ContractStatusLabelPipe } from '../contract-status-label-pipe';
import { ContractStatus, contractStatusOrdered } from './../contract.model';

@Component({
  selector: 'app-contract-status',
  imports: [
    CommonModule,
    TextIcon,
    FontAwesomeModule,
    MatTooltip,
    ContractStatusLabelPipe,
    LucideMinus,
    DisplayableForContractStatusDirective,
  ],
  templateUrl: './contract-status.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./contract-status.scss'],
})
export class ContractStatusComponent {
  faSignature = faSignature;
  status = input.required<ContractStatus>();
  small = input<boolean>(false);

  isComing(other: ContractStatus) {
    if (other === 'CANCELED') {
      return false;
    }

    const currentIndex = contractStatusOrdered.indexOf(this.status());
    const otherIndex = contractStatusOrdered.indexOf(other);
    if (currentIndex === -1 || otherIndex === -1) {
      // If either status is not part of the defined order, do not treat it as "coming"
      return false;
    }
    return currentIndex < otherIndex;
  }

  isCurrent(other: ContractStatus) {
    return this.status() === other;
  }

  isDone(other: ContractStatus) {
    const currentIndex = contractStatusOrdered.indexOf(this.status());
    const otherIndex = contractStatusOrdered.indexOf(other);
    if (currentIndex === -1 || otherIndex === -1) {
      // If either status is not part of the defined order, do not treat it as "coming"
      return false;
    }
    return currentIndex > otherIndex;
  }

  isNext(other: ContractStatus) {
    const currentIndex = contractStatusOrdered.indexOf(this.status());
    const otherIndex = contractStatusOrdered.indexOf(other);
    if (currentIndex === -1 || otherIndex === -1) {
      // If either status is not part of the defined order, do not treat it as "coming"
      return false;
    }
    return currentIndex === otherIndex - 1;
  }

  commitColor(other: ContractStatus): string {
    if (this.status() === 'CANCELED') {
      return 'var(--error-color)';
    }
    if (this.isDone(other) || (this.status() === 'SIGNED' && other === 'SIGNED')) {
      return 'var(--success-color)';
    } else if (this.isCurrent(other) || this.isNext(other)) {
      return 'var(--pending-color)';
    } else {
      return 'white';
    }
  }

  boxColor(other: ContractStatus): string {
    if (this.status() === 'CANCELED') {
      return 'var(--error-color)';
    }
    if (this.isDone(other) || (this.status() === 'SIGNED' && other === 'SIGNED')) {
      return 'var(--success-color)';
    } else if (this.isCurrent(other)) {
      return 'var(--pending-color)';
    } else {
      return 'white';
    }
  }

  getDisplayableStatusParam(otherStatus: ContractStatus): DisplayableForContractStatusInput | null {
    return this.small() ? { currentStatus: this.status(), otherStatus } : null;
  }
}
